import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Grid } from "rsuite";
import { toast } from "react-toastify";
import { PropertyStoreContext } from "../../../contexts/PropertyStoreContext";
import PropertyConfigHeader from "../PropertyConfigHeader";
import PropertyPageCard from "../PropertyPageCard";
import RatePlansDerivedRateTable from "./components/RatePlansDerivedRateTable";
import RatePlanFilters from "./components/RatePlanFilters";
import ExportToExcelButton from "./components/ExportToExcelButton";
import useBaseRatePlansFetcher from "./hooks/useBaseRatePlansFetcher";
import useDerivedRatesData from "./hooks/useDerivedRatesData";
import useExpandedRowIds from "./hooks/useExpandedRowIds";
import { getColumns } from "./columns";
import {
  COPY,
  DEFAULT_STATUS_FILTER,
  POLICY_FILTER,
  SEARCH_DEBOUNCE_MS,
  MAX_VISIBLE_DEPTH,
} from "./constants";
import {
  buildFlatTree,
  applyExpansionState,
  applyFiltersToTree,
  recalculateGaps,
} from "./utils";
import { exportRatePlansToExcel } from "./exportUtils";
import searchIcon from "../../../assets/img/search.svg";

const DerivedRatePlansView = () => {
  const { selectedProperty } = useContext(PropertyStoreContext);
  const propertyId = selectedProperty?.propertyId;

  const [searchQuery, setSearchQuery] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  // Multi-select filters: empty array = no restriction ("all")
  const [distributionFilter, setDistributionFilter] = useState([]);
  const [buyerFilter, setBuyerFilter] = useState([]);
  const [cancellationFilter, setCancellationFilter] = useState(
    POLICY_FILTER.ALL
  );
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);

  // Ref to track debounce timeout for search input
  const searchDebounceRef = useRef(null);

  const { baseRatePlans, loading } = useBaseRatePlansFetcher(propertyId);
  const { derivedRates, clientsById } = useDerivedRatesData(propertyId);
  const { expandedIds, toggleExpand, expandAll, collapseAll, resetExpanded } =
    useExpandedRowIds();

  // Debounced search handler
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;

    // Immediately update input value (instant UI feedback)
    setSearchInputValue(value);

    // Clear previous timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Debounce the actual search query update (filtering)
    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  // Cleanup search debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Property changed: reset all local state (data is fetched automatically by hooks)
  useEffect(() => {
    if (!propertyId) return;
    setSearchQuery("");
    setSearchInputValue("");
    // Clear any pending search debounce
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    resetExpanded();
    setDistributionFilter([]);
    setBuyerFilter([]);
    setCancellationFilter(POLICY_FILTER.ALL);
    setStatusFilter(DEFAULT_STATUS_FILTER);
  }, [propertyId, resetExpanded]);

  // Build complete tree with ALL nodes (always includes all children regardless of expansion)
  // Then apply expansion state and filters to control visibility
  const completeTree = useMemo(() => {
    // Step 1: Build complete tree with all nodes (display-capped at
    // MAX_VISIBLE_DEPTH — deeper levels are only reachable via Excel export)
    const allNodes = buildFlatTree({
      baseRateplans: baseRatePlans,
      derivedRates,
      expandedIds,
      clientsById,
      maxDepth: MAX_VISIBLE_DEPTH,
    });

    // Step 2: Apply expansion state (children of collapsed parents are hidden)
    const withExpansion = applyExpansionState(allNodes);

    // Step 3: Apply filters (non-matching nodes are hidden, parents with matching children are disabled)
    const withFilters = applyFiltersToTree(withExpansion, {
      searchQuery,
      distributionFilter,
      buyerFilter,
      cancellationFilter,
      statusFilter,
    });

    return withFilters;
  }, [
    baseRatePlans,
    derivedRates,
    expandedIds,
    clientsById,
    searchQuery,
    distributionFilter,
    buyerFilter,
    cancellationFilter,
    statusFilter,
  ]);

  // For export: build a complete tree with ALL rows visible (ignore expansion
  // and filters, and no maxDepth — levels hidden by the display cap must
  // still reach the Excel file)
  const allRowsForExport = useMemo(() => {
    // Create a Set with all possible IDs (base rateplans + derived rates)
    const allIds = new Set();
    baseRatePlans.forEach((rp) => allIds.add(rp.id));
    if (Array.isArray(derivedRates)) {
      derivedRates.forEach((dr) => allIds.add(dr.id));
    }

    return buildFlatTree({
      baseRateplans: baseRatePlans,
      derivedRates,
      expandedIds: allIds,
      clientsById,
    });
  }, [baseRatePlans, derivedRates, clientsById]);

  // Extract unique buyers from complete tree for buyer filter dropdown
  const buyerOptions = useMemo(() => {
    const buyersMap = new Map();
    completeTree.forEach((row) => {
      if (row.exclusiveClients && Array.isArray(row.exclusiveClients)) {
        row.exclusiveClients.forEach((client) => {
          if (client.clientId && !buyersMap.has(client.clientId)) {
            buyersMap.set(client.clientId, {
              label: client.clientName || `Client #${client.clientId}`,
              value: client.clientId,
            });
          }
        });
      }
    });
    return Array.from(buyersMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [completeTree]);

  // Build fully-expanded tree with filters to determine all expandable rows
  // (needed for "expand all" to work across all levels in one click)
  const fullyExpandedFilteredTree = useMemo(() => {
    // Create a Set with all possible IDs (base rateplans + derived rates)
    const allIds = new Set();
    baseRatePlans.forEach((rp) => allIds.add(rp.id));
    if (Array.isArray(derivedRates)) {
      derivedRates.forEach((dr) => allIds.add(dr.id));
    }

    // Build tree with everything expanded (same display cap as completeTree)
    const allNodesExpanded = buildFlatTree({
      baseRateplans: baseRatePlans,
      derivedRates,
      expandedIds: allIds,
      clientsById,
      maxDepth: MAX_VISIBLE_DEPTH,
    });

    // Apply expansion state (everything is visible since all are expanded)
    const withExpansion = applyExpansionState(allNodesExpanded);

    // Apply filters to determine which rows would be visible/disabled
    const withFilters = applyFiltersToTree(withExpansion, {
      searchQuery,
      distributionFilter,
      buyerFilter,
      cancellationFilter,
      statusFilter,
    });

    return withFilters;
  }, [
    baseRatePlans,
    derivedRates,
    clientsById,
    searchQuery,
    distributionFilter,
    buyerFilter,
    cancellationFilter,
    statusFilter,
  ]);

  // Visible rows for display (filter by isVisible flag, then recalculate gaps)
  const visibleRows = useMemo(() => {
    const visible = completeTree.filter((row) => row.isVisible);
    // Recalculate gaps based on visible sequence (not complete tree sequence)
    return recalculateGaps(visible);
  }, [completeTree]);

  // All expandable row IDs from fully-expanded filtered tree (for expand all to work in one click)
  // IMPORTANT: Include disabled rows too - they need to be expanded to show their matching children
  const allExpandableRowIds = useMemo(() => {
    const expandable = fullyExpandedFilteredTree
      .filter((row) => row.hasChildren && row.isVisible)
      .map((row) => row.id);

    return expandable;
  }, [fullyExpandedFilteredTree]);

  const allExpanded = useMemo(
    () =>
      allExpandableRowIds.length > 0 &&
      allExpandableRowIds.every((id) => expandedIds.has(id)),
    [allExpandableRowIds, expandedIds]
  );

  // Auto-expand all when filters or search change (so results are clearly visible)
  useEffect(() => {
    // Don't auto-expand on initial load (when all filters are at default values)
    const hasActiveFilters =
      searchQuery !== "" ||
      distributionFilter.length > 0 ||
      buyerFilter.length > 0 ||
      cancellationFilter !== POLICY_FILTER.ALL ||
      statusFilter !== DEFAULT_STATUS_FILTER;

    if (hasActiveFilters && allExpandableRowIds.length > 0) {
      // Use setTimeout to ensure this runs after all state updates are complete
      // This avoids race conditions with the tree computation
      const timeoutId = setTimeout(() => {
        expandAll(allExpandableRowIds);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [
    searchQuery,
    distributionFilter,
    buyerFilter,
    cancellationFilter,
    statusFilter,
    baseRatePlans,
    derivedRates,
    allExpandableRowIds,
    expandAll,
  ]);

  const handleToggleExpandAll = useCallback(() => {
    if (allExpanded) {
      collapseAll();
    } else {
      // Use allExpandableRowIds to expand ALL levels, not just visible ones
      expandAll(allExpandableRowIds);
    }
  }, [allExpanded, collapseAll, expandAll, allExpandableRowIds]);

  const handleExport = useCallback(() => {
    try {
      const propertyName = selectedProperty?.propertyName || "Property";
      // Export ALL data (all parents and children), ignoring filters and expansion state
      exportRatePlansToExcel(allRowsForExport, propertyName);
    } catch (error) {
      toast.error(COPY.ERROR_EXPORT_FAILED);
      globalThis.R7Insight?.error?.("Rate plan export failed", {
        message: error?.message,
      });
    }
  }, [allRowsForExport, selectedProperty]);

  const columns = useMemo(
    () =>
      getColumns({
        onToggleExpand: toggleExpand,
        onToggleExpandAll: handleToggleExpandAll,
        allExpanded,
      }),
    [toggleExpand, handleToggleExpandAll, allExpanded]
  );

  return (
    <Grid fluid className="property-config-page rate-plans-derived-rate-page">
      <PropertyConfigHeader title={COPY.PAGE_TITLE} />
      <PropertyPageCard
        toolbarLeft={
          <>
            <div className="property-page-card__search-wrapper">
              <div className="property-page-card__search">
                <img
                  src={searchIcon}
                  alt=""
                  className="property-page-card__search-icon"
                />
                <input
                  type="text"
                  className="property-page-card__search-input"
                  placeholder={COPY.SEARCH_PLACEHOLDER}
                  aria-label={COPY.SEARCH_ARIA_LABEL}
                  value={searchInputValue}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            <div className="property-page-card__toolbar-separator" />
            <RatePlanFilters
              distributionFilter={distributionFilter}
              onDistributionChange={setDistributionFilter}
              buyerFilter={buyerFilter}
              onBuyerChange={setBuyerFilter}
              buyerOptions={buyerOptions}
              cancellationFilter={cancellationFilter}
              onCancellationChange={setCancellationFilter}
              statusFilter={statusFilter}
              onStatusChange={setStatusFilter}
            />
          </>
        }
        toolbarRight={
          <ExportToExcelButton
            onClick={handleExport}
            disabled={allRowsForExport.length === 0}
          />
        }
      >
        <RatePlansDerivedRateTable
          columns={columns}
          data={visibleRows}
          loading={loading}
          loadingMessage={COPY.LOADING}
          emptyMessage={COPY.EMPTY}
          className="rate-plans-derived-rate-page__table"
        />
      </PropertyPageCard>
    </Grid>
  );
};

export default DerivedRatePlansView;
